import { Text, Button } from "components/commons";
import { locale } from "localization/en";
import moment from "moment";
import React, { useMemo } from "react";
import { formatAmount } from "utils";
import Skeleton from "@material-ui/lab/Skeleton";
import styles from "./plb-credit-card.module.scss";

const PlbCreditCard = ({ fleet, latestBilling, loading, onPayNow }) => {
  const availableLimit = Number(fleet.availableLimit) || 0;
  const creditLimit = Number(fleet.creditLimit) || 0;

  const consumedPercent = useMemo(() => {
    if (!creditLimit || availableLimit >= creditLimit) {
      return 0;
    }
    const consumed = creditLimit - availableLimit;
    return Math.min(100, Math.max(0, (consumed / creditLimit) * 100));
  }, [availableLimit, creditLimit]);

  const statementMonth = latestBilling?.startDate
    ? moment(latestBilling.startDate).format("MMMM")
    : "";

  const onViewBillingStatement = () => {
    if (latestBilling?.soaUrl) {
      window.open(latestBilling.soaUrl, "_blank");
    }
  };

  return (
    <div className={styles.plbCreditCard}>
      <div className={styles.creditSummary}>
        <svg
          className={styles.decorativeLayers}
          viewBox="0 0 173.9 114.2"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <defs>
            <linearGradient id="plbCreditPathGradient" x1="1" y1="0" x2="0" y2="0">
              <stop offset="0.73" stopColor="#805ecb" />
              <stop offset="1" stopColor="#484891" />
            </linearGradient>
          </defs>
          <path
            fill="url(#plbCreditPathGradient)"
            d="M29,0 H114 C149,0 157,35 157,65 C157,95 164,110 173.9,114.2 H29 Z"
          />
          <path
            fill="url(#plbCreditPathGradient)"
            d="M0,0 H85 C120,0 128,35 128,65 C128,95 135,110 144.9,114.2 H0 Z"
          />
        </svg>
        <div className={styles.creditSummaryHeader}>
          <div>
            <Text className={styles.label}>{locale.availableCredit}</Text>
            {loading ? (
              <Skeleton variant="rect" className={styles.loaderAmount} />
            ) : (
              <Text className={styles.amount}>{formatAmount(availableLimit)}</Text>
            )}
          </div>
          <div className={styles.creditLimit}>
            <Text className={styles.label}>{locale.creditLimit}</Text>
            {loading ? (
              <Skeleton variant="rect" className={styles.loaderAmount} />
            ) : (
              <Text className={styles.limitAmount}>{formatAmount(creditLimit)}</Text>
            )}
          </div>
        </div>
        <div className={styles.progressBar}>
          <div className={styles.progressBarFill} style={{ width: `${consumedPercent}%` }} />
        </div>
      </div>
      <div className={styles.billingSummary}>
        <div className={styles.billingDetails}>
          <Text className={styles.statementLabel}>
            {statementMonth ? `${statementMonth} ${locale.statement}` : locale.statement}
          </Text>
          {loading ? (
            <Skeleton variant="rect" className={styles.loaderAmount} />
          ) : (
            <Text className={styles.statementAmount}>
              {formatAmount(latestBilling?.billingAmount || 0)}
            </Text>
          )}
          {latestBilling?.dueDate && (
            <Text className={styles.dueDate}>
              {`${locale.dueOn} ${moment(latestBilling.dueDate).format("MMM DD, YYYY")}`}
            </Text>
          )}
        </div>
        <div className={styles.billingActions}>
          <Button primary className={styles.payNowButton} onClick={onPayNow}>
            {locale.payNow}
          </Button>
          {latestBilling?.soaUrl && (
            <span className={styles.viewStatementLink} onClick={onViewBillingStatement}>
              {locale.viewBillingStatement}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default PlbCreditCard;
